import {
  AvatarCurrency,
  AvatarProps,
  FormAutocompleteField,
  FormAutocompleteFieldProps,
} from '@razz-team/ui-web';
import { ApiBeneficiary } from '../../openapi';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { BeneficiaryListItem } from '../ListItem/BeneficiaryListItem';

export interface BeneficiaryFieldProps<
  Multiple extends boolean = false,
  DisableClearable extends boolean = false,
> extends Omit<
  FormAutocompleteFieldProps<string, Multiple, DisableClearable>,
  'options' | 'getOptionLabel'
> {
  beneficiaries: ApiBeneficiary[] | undefined;
}

export function BeneficiaryField<
  Multiple extends boolean = false,
  DisableClearable extends boolean = false,
>({ beneficiaries, ...props }: BeneficiaryFieldProps<Multiple, DisableClearable>) {
  const { t } = useTranslation();
  const options = useMemo(
    () => beneficiaries?.map((it) => it.beneficiaryId) ?? [],
    [beneficiaries],
  );
  const getOptionLabel = useCallback(
    (option: string) => {
      const beneficiary = beneficiaries?.find((it) => it.beneficiaryId === option);

      switch (beneficiary?.type) {
        case 'INDIVIDUAL':
          return beneficiary.displayName ?? beneficiary.individualName;
        case 'BUSINESS':
          return beneficiary.displayName ?? beneficiary.businessName;
        case 'CRYPTO':
          return (
            beneficiary.displayName ??
            beneficiary.beneficiaryName ??
            beneficiary.accountNumber.value
          );
        default:
          return t('Unknown beneficiary');
      }
    },
    [beneficiaries, t],
  );
  const renderOption = useCallback(
    (optionProps: React.HTMLAttributes<HTMLLIElement>, option: string) => {
      const beneficiary = beneficiaries?.find((it) => it.beneficiaryId === option);
      return beneficiary ? (
        <BeneficiaryListItem beneficiary={beneficiary} {...optionProps} key={option} />
      ) : null;
    },
    [beneficiaries],
  );
  const renderAvatar = useCallback(
    (beneficiaryId: string, avatarProps: Partial<AvatarProps>) => {
      const beneficiary = beneficiaries?.find((a) => a.beneficiaryId === beneficiaryId);
      return beneficiary ? (
        <AvatarCurrency currency={beneficiary.currency} {...avatarProps} />
      ) : undefined;
    },
    [beneficiaries],
  );

  return (
    <FormAutocompleteField<string, Multiple, DisableClearable>
      options={options}
      getOptionLabel={getOptionLabel}
      renderOption={renderOption}
      renderAvatar={renderAvatar}
      {...props}
    />
  );
}
